import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Coffee, Flame } from "lucide-react";

const PRESETS = [200, 500, 1000, 2500];

export default function TipJarModal({ artistId, artistName }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(500);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!user) { toast.error("Sign in to tip"); nav("/login"); return; }
    const cents = custom ? Math.round(parseFloat(custom) * 100) : amount;
    if (!cents || cents < 100) { toast.error("Minimum tip is $1"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "tip", item_id: artistId, amount_cents: cents, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`tip-btn-${artistId}`}
                variant="outline"
                className="rounded-none border-[#FF4500]/40 text-[#FF4500] hover:bg-[#FF4500]/10 bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4">
          <Coffee className="h-4 w-4 mr-2" /> Tip artist
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121212] border-[#2A2A2A] rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Flame className="h-5 w-5 text-[#FF4500]" /> Tip {artistName}
          </DialogTitle>
        </DialogHeader>
        <p className="font-mono text-xs text-gray-400">100% of tips go straight to the artist. Because they earned it.</p>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`tip-preset-${p}`}
                    onClick={() => { setAmount(p); setCustom(""); }}
                    className={`h-11 font-mono text-sm border transition-colors ${amount === p && !custom ? "border-[#FF4500] bg-[#FF4500]/10 text-[#FF4500]" : "border-[#2A2A2A] hover:border-[#FF4500]/60"}`}>
              ${p / 100}
            </button>
          ))}
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Custom amount (USD)</label>
          <Input data-testid="tip-custom" type="number" min="1" step="0.5" value={custom}
                 onChange={(e) => setCustom(e.target.value)} placeholder="e.g. 7.50"
                 className="mt-1 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
        </div>
        <Button data-testid="tip-send" disabled={busy} onClick={send}
                className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 mt-2">
          {busy ? "Redirecting..." : `Send tip · $${((custom ? parseFloat(custom || 0) * 100 : amount) / 100).toFixed(2)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
